import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  useGridApiContext,
  useGridSelector,
  gridFilterModelSelector,
  gridFilterableColumnDefinitionsSelector,
  GridLogicOperator,
  type GridFilterModel,
  type GridFilterItem,
  type GridColDef,
} from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useRef } from 'react';
import { isNegated } from '@/utils/negatedFilterItem';

declare module '@mui/x-data-grid' {
  // eslint-disable-next-line unicorn/prevent-abbreviations
  interface FilterPanelPropsOverrides {
    onNegationChange?: (itemId: string | number, negated: boolean, filterModel: GridFilterModel) => void;
  }
}

type OnNegationChange = (itemId: string | number, negated: boolean, filterModel: GridFilterModel) => void;

const IncludeExcludeSelect = ({ negated, onChange }: { negated: boolean; onChange: (negated: boolean) => void }) => (
  <Select
    size='small'
    value={negated ? 'exclude' : 'include'}
    onChange={(event: SelectChangeEvent<string>) => onChange(event.target.value === 'exclude')}
    renderValue={value => (value === 'include' ? 'Include' : 'Exclude')}
    sx={{ width: 110, flexShrink: 0, '& .MuiSelect-select': { fontSize: '1rem' } }}
  >
    <MenuItem value='include'>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CheckIcon sx={{ color: 'success.main', fontSize: '1.1rem' }} />
        <Typography sx={{ fontWeight: 600, color: 'success.main' }}>Include</Typography>
      </Box>
    </MenuItem>
    <MenuItem value='exclude'>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CloseIcon sx={{ color: 'error.main', fontSize: '1.1rem' }} />
        <Typography sx={{ fontWeight: 600, color: 'error.main' }}>Exclude</Typography>
      </Box>
    </MenuItem>
  </Select>
);

const FilterRow = ({
  item,
  columns,
  showLogicOperator,
  isFirstLogicOperator,
  hasMultipleItems,
  logicOperator,
  onLogicOperatorChange,
  onNegationToggle,
  onUpdate,
  onDelete,
}: {
  item: GridFilterItem;
  columns: GridColDef[];
  showLogicOperator: boolean;
  isFirstLogicOperator: boolean;
  hasMultipleItems: boolean;
  logicOperator: GridLogicOperator;
  onLogicOperatorChange: (operator: GridLogicOperator) => void;
  onNegationToggle: (item: GridFilterItem, negated: boolean) => void;
  onUpdate: (updated: GridFilterItem) => void;
  onDelete: (item: GridFilterItem) => void;
}) => {
  const apiReference = useGridApiContext();
  const column = columns.find(col => col.field === item.field);
  const operators = column?.filterOperators ?? [];
  const currentOperator = operators.find(op => op.value === item.operator);
  const InputComponent = currentOperator?.InputComponent;

  const handleColumnChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newColumn = columns.find(col => col.field === event.target.value);
      const newOperators = newColumn?.filterOperators ?? [];
      const matchingOperator = newOperators.find(op => op.value === item.operator) ?? newOperators[0];
      const eraseValue =
        !matchingOperator?.InputComponent ||
        matchingOperator.InputComponent !== currentOperator?.InputComponent ||
        newColumn?.type !== column?.type;
      onUpdate({
        ...item,
        field: event.target.value,
        operator: matchingOperator?.value ?? '',
        value: eraseValue ? undefined : item.value,
      });
    },
    [columns, column, currentOperator, item, onUpdate]
  );

  const handleOperatorChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newOperator = operators.find(op => op.value === event.target.value);
      const eraseValue = !newOperator?.InputComponent || newOperator.InputComponent !== currentOperator?.InputComponent;
      onUpdate({ ...item, operator: event.target.value, value: eraseValue ? undefined : item.value });
    },
    [operators, currentOperator, item, onUpdate]
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1 }}>
      <IconButton size='small' onClick={() => onDelete(item)} sx={{ flexShrink: 0 }}>
        <ClearIcon fontSize='small' />
      </IconButton>

      {hasMultipleItems && (
        <Box sx={{ width: 80, flexShrink: 0 }}>
          {showLogicOperator ? (
            <TextField
              select
              size='small'
              value={logicOperator}
              onChange={event =>
                onLogicOperatorChange(event.target.value === 'or' ? GridLogicOperator.Or : GridLogicOperator.And)
              }
              disabled={!isFirstLogicOperator}
              fullWidth
            >
              <MenuItem value={GridLogicOperator.And}>And</MenuItem>
              <MenuItem value={GridLogicOperator.Or}>Or</MenuItem>
            </TextField>
          ) : undefined}
        </Box>
      )}

      <IncludeExcludeSelect negated={isNegated(item)} onChange={negated => onNegationToggle(item, negated)} />

      <TextField
        select
        size='small'
        label='Columns'
        value={item.field}
        onChange={handleColumnChange as (event: React.ChangeEvent<HTMLInputElement>) => void}
        sx={{ width: 150, flexShrink: 0 }}
      >
        {columns.map(col => (
          <MenuItem key={col.field} value={col.field}>
            {col.headerName ?? col.field}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size='small'
        label='Operator'
        value={item.operator}
        onChange={handleOperatorChange as (event: React.ChangeEvent<HTMLInputElement>) => void}
        sx={{ width: 150, flexShrink: 0 }}
      >
        {operators.map(op => (
          <MenuItem key={op.value} value={op.value}>
            {op.label ??
              apiReference.current.getLocaleText(
                `filterOperator${op.value.charAt(0).toUpperCase()}${op.value.slice(1)}` as never
              )}
          </MenuItem>
        ))}
      </TextField>

      <Box sx={{ flex: 1, minWidth: 150, '& .MuiInputBase-root': { height: 40 } }}>
        {InputComponent ? (
          <InputComponent
            apiRef={apiReference}
            item={item}
            applyValue={onUpdate}
            {...currentOperator.InputComponentProps}
          />
        ) : undefined}
      </Box>
    </Box>
  );
};

export const NegationFilterPanel = ({ onNegationChange }: { onNegationChange?: OnNegationChange }) => {
  const apiReference = useGridApiContext();
  const filterModel = useGridSelector(apiReference, gridFilterModelSelector);
  const columns = useGridSelector(apiReference, gridFilterableColumnDefinitionsSelector);

  // Stable placeholder — same pattern as MUI's GridFilterPanel.
  // Keeps the same object identity so React doesn't remount the row on commit.
  const placeholderReference = useRef<GridFilterItem | undefined>(undefined);
  const displayItems = useMemo(() => {
    if (filterModel.items.length > 0) {
      placeholderReference.current = undefined;
      return filterModel.items;
    }
    if (!placeholderReference.current) {
      const firstColumn = columns.find(col => col.filterOperators?.length);
      if (firstColumn?.filterOperators?.[0]) {
        placeholderReference.current = {
          id: globalThis.crypto.randomUUID(),
          field: firstColumn.field,
          operator: firstColumn.filterOperators[0].value,
        };
      }
    }
    return placeholderReference.current ? [placeholderReference.current] : [];
  }, [filterModel.items, columns]);

  const buildModelWith = useCallback(
    (updatedItem: GridFilterItem) => {
      const currentItems = filterModel.items;
      const exists = currentItems.some(existing => existing.id === updatedItem.id);
      const items = exists
        ? currentItems.map(existing => (existing.id === updatedItem.id ? updatedItem : existing))
        : [...currentItems, updatedItem];
      return { ...filterModel, items };
    },
    [filterModel]
  );

  const handleUpdate = useCallback(
    (updated: GridFilterItem) => apiReference.current.upsertFilterItem(updated),
    [apiReference]
  );

  const handleNegationToggle = useCallback(
    (item: GridFilterItem, negated: boolean) => {
      const tagged = { ...item, negated: negated || undefined };
      apiReference.current.upsertFilterItem(tagged);
      onNegationChange?.(tagged.id!, negated, buildModelWith(tagged));
    },
    [apiReference, onNegationChange, buildModelWith]
  );

  const handleDelete = useCallback(
    (item: GridFilterItem) => {
      if (filterModel.items.length === 0) return;
      apiReference.current.deleteFilterItem(item);
    },
    [apiReference, filterModel.items.length]
  );

  const handleLogicOperatorChange = useCallback(
    (operator: GridLogicOperator) => apiReference.current.setFilterLogicOperator(operator),
    [apiReference]
  );

  const addFilter = useCallback(() => {
    const firstColumn = columns.find(col => col.filterOperators?.length);
    if (!firstColumn?.filterOperators?.[0]) return;
    apiReference.current.upsertFilterItem({
      id: globalThis.crypto.randomUUID(),
      field: firstColumn.field,
      operator: firstColumn.filterOperators[0].value,
    });
  }, [apiReference, columns]);

  const handleRemoveAll = useCallback(
    () => apiReference.current.setFilterModel({ ...filterModel, items: [] }),
    [apiReference, filterModel]
  );

  return (
    <Box sx={{ width: 800, maxWidth: 'calc(100vw - 32px)', pt: 1.5 }}>
      {displayItems.map((item, index) => (
        <FilterRow
          key={item.id ?? index}
          item={item}
          columns={columns}
          showLogicOperator={index > 0}
          isFirstLogicOperator={index === 1}
          hasMultipleItems={displayItems.length > 1}
          logicOperator={filterModel.logicOperator ?? GridLogicOperator.And}
          onLogicOperatorChange={handleLogicOperatorChange}
          onNegationToggle={handleNegationToggle}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      ))}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          mt: 1,
        }}
      >
        <Button size='small' startIcon={<AddIcon />} onClick={addFilter} sx={{ fontSize: '0.9rem' }}>
          Add filter
        </Button>
        <Button size='small' startIcon={<DeleteForeverIcon />} onClick={handleRemoveAll} sx={{ fontSize: '0.9rem' }}>
          Remove all
        </Button>
      </Box>
    </Box>
  );
};
